import React from 'react';
import PropTypes from 'prop-types';
import ProjectFundsElement from './ProjectFundsElement';

import { gettext } from 'utils/text';
import { decodeFormValue } from 'utils/forms';

class ProjectFunds extends React.Component {
    static propTypes = {
        fieldName: PropTypes.string.isRequired,
        requiredElementDescription: PropTypes.string,
        requiredElementDefault: PropTypes.number,
        maxElements: PropTypes.number,
        fieldValue: PropTypes.string,
    };

    static defaultProps = {
        requiredElementDescription: undefined,
        requiredElementDefault: 0,
        maxElements: 25,
        fieldValue: undefined,
    };

    static MAX_AMOUNT = 1000000000; // Keep the numbers somewhat reasonable

    static createElementData(description = '', amount = '0', required = false) {
        const elementData = {
            description,
            amount,
        };
        if (required) {
            elementData.required = true;
        }
        return elementData;
    }

    static elementDataNotEmpty(elementData) {
        return elementData.required || (elementData.description && Number(elementData.amount) >= 1);
    }

    constructor(props) {
        super(props);
        const initialState = {};
        const initialValue = props.fieldValue && JSON.parse(decodeFormValue(props.fieldValue));
        if (initialValue && Object.keys(initialValue).length) {
            initialState.elementDatas = initialValue;
        } else if (props.requiredElementDescription) {
            initialState.elementDatas = {
                0: ProjectFunds.createElementData(
                    props.requiredElementDescription,
                    String(props.requiredElementDefault),
                    true,
                ),
            };
        } else {
            initialState.elementDatas = {
                0: ProjectFunds.createElementData(),
            };
        }
        this.state = initialState;
    }

    onChange(e, order, key) {
        e.preventDefault();
        const oldElementData = this.state.elementDatas[order];
        const newElementData = { ...oldElementData };
        let newValue = e.target.value;

        if (key === 'description') {
            // Allow only one element with requiredElementDescription (created in constructor)
            if (newValue === this.props.requiredElementDescription) return;
            // Don't allow changing the required elements description
            if (newElementData.required) return;
            newElementData.description = newValue;
        } else if (key === 'amount') {
            let maxAmount = ProjectFunds.MAX_AMOUNT;
            if (newElementData.required) {
                maxAmount = this.props.requiredElementDefault;
            }
            // Try converting to a positive integer and keep under max
            if (newValue) {
                newValue = String(Math.abs(Math.round(Number(newValue))));
                if (newValue > maxAmount) {
                    newValue = String(maxAmount);
                }
            }

            newElementData.amount = newValue;
        }

        this.setState({
            elementDatas: {
                ...this.state.elementDatas,
                [order]: newElementData,
            },
        });
    }

    getTotal() {
        return Math.round(
            Object.values(this.state.elementDatas).reduce(
                (total, elementData) => total + Number(elementData.amount),
                0,
            ),
        );
    }

    generateJSON() {
        // Convert all numbers to number types and do basic filtering
        const cleanedData = {};
        Object.entries(this.state.elementDatas).forEach(([order, elementData]) => {
            if (ProjectFunds.elementDataNotEmpty(elementData)) {
                cleanedData[order] = {
                    description: elementData.description,
                    amount: Math.round(Number(elementData.amount)),
                };
                if (elementData.required) {
                    cleanedData[order].required = true;
                }
            }
        });
        return JSON.stringify(cleanedData);
    }

    canAddElement() {
        return Object.keys(this.state.elementDatas).length < this.props.maxElements;
    }

    addElement() {
        if (!this.canAddElement()) {
            return;
        }
        const newOrder = String(Math.max(...Object.keys(this.state.elementDatas).map(Number)) + 1);
        const newElementData = ProjectFunds.createElementData();
        this.setState({
            elementDatas: {
                ...this.state.elementDatas,
                [newOrder]: newElementData,
            },
        });
    }

    removeElement(order) {
        // Don't allow removing required elements
        if (this.state.elementDatas[order].required) {
            return;
        }
        // Don't allow deleting all elements
        if (Object.keys(this.state.elementDatas).length <= 1) {
            return;
        }
        const { [order]: _removedElement, ...newElementDatas } = this.state.elementDatas;

        this.setState({
            elementDatas: newElementDatas,
        });
    }

    render() {
        const sortedOrders = Object.keys(this.state.elementDatas)
            .map(Number)
            .sort((a, b) => a - b) // Sort numerically not alphabetically
            .map(String);
        const sortedElementDatas = sortedOrders.map((order) => ({
            ...this.state.elementDatas[order],
            order,
        }));
        const onlyOneElementData = sortedOrders.length === 1;
        return (
            <div className="project-funds">
                {sortedElementDatas.map((elementData) => (
                    <ProjectFundsElement
                        key={elementData.order}
                        description={elementData.description}
                        amount={elementData.amount}
                        order={elementData.order}
                        onChange={(...args) => this.onChange(...args)}
                        removeElement={(order) => this.removeElement(order)}
                        lockDescription={elementData.required}
                        canDelete={!(elementData.required || onlyOneElementData)}
                        maxAmount={elementData.required ? this.props.requiredElementDefault : ProjectFunds.MAX_AMOUNT}
                    />
                ))}
                <div className="project-funds__element">
                    <div className="mt-1">
                        {this.canAddElement() && (
                            <button
                                type="button"
                                className="text-btn text-btn--underlined"
                                onClick={() => this.addElement()}
                            >
                                {`+ ${gettext('Add more')}`}
                            </button>
                        )}
                    </div>
                    <div className="mt-1">
                        <b>{gettext('Total')}:</b>&nbsp;{this.getTotal()},-
                    </div>
                </div>
                <input type="hidden" name={this.props.fieldName} value={this.generateJSON()} />
            </div>
        );
    }
}

export default ProjectFunds;



// WEBPACK FOOTER //
// ./src/components/ProjectFunds.js